import { scanNalUnitPayloads, unescapeRbsp } from "./annexb";
import type { VideoCodec } from "./VideoCodec";

/**
 * Minimal H.265 (HEVC) Annex B bitstream inspection.
 *
 * Implements only the byte-level parsing needed to drive a WebCodecs
 * `VideoDecoder` in Annex B mode: splitting a frame into NAL units, detecting
 * keyframes (IRAP pictures), and deriving the codec string from the SPS's
 * profile-tier-level. It neither decodes video nor parses variable-length
 * (Exp-Golomb) fields — the profile-tier-level occupies fixed positions at
 * the start of the SPS (after emulation-prevention unescaping), and the
 * decoder reads everything else from the in-band parameter sets.
 *
 * The start-code scan shared with H.264 lives in `annexb.ts`; this module
 * adds the H.265 specifics: a 2-byte NAL header whose first byte carries a
 * 6-bit type in bits 6..1 (ITU-T H.265 section 7.3.1.2).
 */

/**
 * H.265 NAL unit types (ITU-T H.265 table 7-1) relevant to this module.
 * A plain const object (not an enum) because NAL headers can carry any
 * 6-bit type value, so comparisons happen against plain numbers.
 */
export const NalUnitType = {
  TrailR: 1,
  BlaWLp: 16,
  IdrWRadl: 19,
  IdrNLp: 20,
  CraNut: 21,
  VideoParameterSet: 32,
  SequenceParameterSet: 33,
  PictureParameterSet: 34,
} as const;

export interface NalUnit {
  /** `nal_unit_type` from the unit's 2-byte header. */
  type: number;
  /** The unit's bytes: header bytes included, start code excluded. */
  data: Uint8Array;
}

const NAL_UNIT_TYPE_SHIFT = 1;
const NAL_UNIT_TYPE_MASK = 0x3f;

// IRAP (intra random access point) NAL unit types span BLA_W_LP (16) through
// RSV_IRAP_VCL23 (23) — ITU-T H.265 table 7-1. Any VCL NAL in this range
// makes the frame a clean decoder entry point.
const IRAP_TYPE_MIN = 16;
const IRAP_TYPE_MAX = 23;

function nalType(headerByte: number): number {
  return (headerByte >> NAL_UNIT_TYPE_SHIFT) & NAL_UNIT_TYPE_MASK;
}

/**
 * Split Annex B-framed H.265 data into NAL units.
 *
 * Handles both 3-byte (`00 00 01`) and 4-byte (`00 00 00 01`) start codes.
 * Returned `data` views alias the input buffer; they are not copies.
 */
export function findNalUnits(data: Uint8Array): NalUnit[] {
  return scanNalUnitPayloads(data).map((payload) => ({
    type: nalType(payload[0]),
    data: payload,
  }));
}

/**
 * Whether the frame contains an IRAP picture (IDR, CRA, or BLA), i.e. a clean
 * decoder entry point. In WebCodecs terms, such a frame is an
 * `EncodedVideoChunk` of type "key"; all other frames are "delta".
 */
export function isKeyframe(data: Uint8Array): boolean {
  return scanNalUnitPayloads(data).some((payload) => {
    const type = nalType(payload[0]);
    return type >= IRAP_TYPE_MIN && type <= IRAP_TYPE_MAX;
  });
}

// Profile-tier-level layout within the unescaped SPS payload (after the
// 2-byte NAL header): byte 0 packs sps_video_parameter_set_id /
// sps_max_sub_layers_minus1 / sps_temporal_id_nesting_flag; the PTL follows
// at fixed positions (ITU-T H.265 sections 7.3.2.2.1 and 7.3.3).
const PTL_PROFILE_OFFSET = 1; // general_profile_space/tier_flag/profile_idc
const PTL_COMPAT_OFFSET = 2; // general_profile_compatibility_flags (4 bytes)
const PTL_CONSTRAINT_OFFSET = 6; // general constraint flags (6 bytes)
const PTL_LEVEL_OFFSET = 12; // general_level_idc
const PTL_BYTE_LENGTH = 13;

/**
 * Derive the WebCodecs codec string (`hev1.*`, ISO/IEC 14496-15 Annex E) for
 * the frame, or `undefined` when the frame carries no (usable) SPS.
 *
 * Unlike H.264's three fixed SPS bytes, the H.265 profile-tier-level spans 12
 * bytes that regularly contain zero runs, so emulation-prevention escaping
 * must be undone before reading the fixed positions.
 */
export function codecStringFromSps(data: Uint8Array): string | undefined {
  const sps = scanNalUnitPayloads(data).find(
    (payload) => nalType(payload[0]) === NalUnitType.SequenceParameterSet,
  );
  if (sps === undefined) {
    return undefined;
  }
  // Unescape past the 2-byte NAL header; read a couple of bytes of slack so a
  // truncated SPS is detected by length rather than by NaN arithmetic.
  const ptl = unescapeRbsp(sps.subarray(2), PTL_BYTE_LENGTH + 2);
  if (ptl.length < PTL_BYTE_LENGTH) {
    return undefined;
  }

  const profileSpace = (ptl[PTL_PROFILE_OFFSET] >> 6) & 0x03;
  const tierFlag = (ptl[PTL_PROFILE_OFFSET] >> 5) & 0x01;
  const profileIdc = ptl[PTL_PROFILE_OFFSET] & 0x1f;

  // Compatibility flags are rendered with their 32 bits in reverse order,
  // as hex without leading zeros (ISO/IEC 14496-15 E.3).
  let compatibilityReversed = 0;
  for (let bit = 0; bit < 32; bit++) {
    const byte = ptl[PTL_COMPAT_OFFSET + (bit >> 3)];
    if ((byte >> (7 - (bit % 8))) & 0x01) {
      compatibilityReversed |= 1 << bit;
    }
  }

  // Constraint bytes are rendered per byte, with trailing zero bytes omitted.
  const constraints: string[] = [];
  for (let i = 5; i >= 0; i--) {
    const byte = ptl[PTL_CONSTRAINT_OFFSET + i];
    if (byte === 0 && constraints.length === 0) {
      continue;
    }
    constraints.unshift(byte.toString(16).toUpperCase().padStart(2, "0"));
  }

  const parts = [
    "hev1",
    `${["", "A", "B", "C"][profileSpace]}${profileIdc}`,
    (compatibilityReversed >>> 0).toString(16).toUpperCase(),
    `${tierFlag === 0 ? "L" : "H"}${ptl[PTL_LEVEL_OFFSET]}`,
    ...constraints,
  ];
  return parts.join(".");
}

/**
 * The H.265 implementation of the {@link VideoCodec} seam: Annex B-framed,
 * IRAP keyframes, and an `hev1.*` codec string parsed from the keyframe's
 * SPS profile-tier-level. Main profile L3.1 (`hev1.1.6.L93.B0`) is the
 * capability probe because it is the most broadly supported configuration.
 */
export const h265: VideoCodec = {
  formats: ["h265", "hevc"],
  framing: "annexb",
  probeCodecString: "hev1.1.6.L93.B0",
  isKeyframe,
  codecStringFromKeyframe: codecStringFromSps,
};
